import { effectiveAge , resolveSchoolYear } from "./age.js";
import { resolveRungByYear, sanitizeYears } from "./ladder.js";
import { cmsMathsBands, cmsMathsSlotItems, cmsMathsTypeGrades, cmsMathsTypeL3, cmsMathsTypeLevels, cmsMathsTypeMix, cmsMathsTypeParams } from "./content.js";
// Question generation for the two Learn & Earn maths games:
//   - TT Streaks: a fixed times-tables drill, tables chosen by a parent (2–12).
//   - Maths Workout: a mixed-operation session that scales with an age band.
// Both return plain { prompt, answer } objects — Tests.jsx owns all session/scoring state.

// Presentation helper for EVERY surface that renders a generated maths prompt for
// answering (Tests.jsx's TT Streaks/Maths Workout screens, the Daily Question overlay,
// Snack Attack's Brain Warm-Up). A prompt that already reads as a sentence question
// ("...How much does each get?") is left alone; a bare arithmetic prompt ("7 × 6") gets
// " = ?" appended so it still reads as a fill-in-the-blank. Lived in Tests.jsx originally
// — moved here (W1.12, Aug 2026) because two other render sites appended " = ?"
// unconditionally, which produced things like "What is x? = ?" and "What is 20% of 80 = ?"
// on sentence-style prompts. The generators' own sentence prompts now all end with "?"
// (same change), so this guard is what keeps them clean everywhere they render.
// Contains-"?" rather than ends-with-"?" since the ladder types (Aug 2026): the number
// bonds prompts ("? + 3 = 10") carry their blank mid-prompt, and "? + 3 = 10 = ?" is the
// same class of double-marking W1.12 closed. A superset check with zero change to any
// pre-existing prompt: everything that ended with "?" also contains one, and everything
// that got " = ?" appended before contained none.
export function withAnswerPrompt(prompt) {
  return String(prompt || "").includes("?") ? prompt : `${prompt} = ?`;
}

export const DEFAULT_TT_TABLES = [2, 3, 4, 5, 10];

// TT Streaks year presets (docs/DIFFICULTY_LADDER_SCOPE.md — the one ladder touch TT
// gets; Alan, 26 Aug: chips in, no other TT change). Straight from the national
// curriculum: Y2 introduces 2/5/10; Y3 adds 3/4/8; Y4 is all tables to 12 — the year
// of the multiplication check. One tap applies the set; hand-picking stays.
export const TT_YEAR_PRESETS = [
  { id: "y2", tables: [2, 5, 10] },
  { id: "y3", tables: [2, 3, 4, 5, 8, 10] },
  { id: "y4", tables: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
];

// Default session/test length for every Learn & Earn game, a parent can always override
// per game from Parent Mode -> Learn & Earn. Standardised to 15 across the board (TT
// Streaks, Maths Workout, and Vocabulary via VOCAB_TEST_SIZE in constants.js) so a new
// household starts every subject at the same length rather than three different defaults.
export const TT_SESSION_SIZE = 15;

export const MATHS_WORKOUT_SESSION_SIZE = 15;

export const MATHS_BANDS = {
  foundations: { label: "Foundations", blurb: "Adding and taking away up to 20", minAge: 5, maxAge: 7 },
  builder: { label: "Builder", blurb: "Times tables, adding and taking away to 100", minAge: 8, maxAge: 9 },
  challenger: { label: "Challenger", blurb: "Bigger multiplication, division, fractions of amounts", minAge: 10, maxAge: 11 },
  strategist: { label: "Strategist", blurb: "Decimals, percentages, negative numbers, order of operations", minAge: 12, maxAge: 14 },
  genius: { label: "Genius", blurb: "Solving for x, substitution, powers, multi-step mental maths — no calculator needed", minAge: 15, maxAge: 18 },
};

export const MATHS_BAND_ORDER = ["foundations", "builder", "challenger", "strategist", "genius"];

// CMS-aware view of the maths bands (docs/CMS_SCOPE.md §4.4 / docs/MATHS_PARAMETERIZATION_SCOPE.md).
// Deliberately PER-BAND fallback, not whole-collection swap — the opposite pattern
// from spelling/vocab categories, and on purpose (Alan, Aug 2026: "that's a weird
// design that's guaranteed to cause problems"). The five original bands together
// cover the entire age range with no gaps; a whole-collection swap means a partial or
// accidental publish — even one missing band — silently reassigns every child in that
// age gap to the nearest remaining band, with no error, nothing visibly wrong, just
// quietly incorrect difficulty until someone notices. Per-band fallback closes that
// off structurally: every one of the five original bands is ALWAYS present, whether
// or not the CMS has ever touched it. A CMS entry for an existing band id overrides
// just that band's own label/blurb/age-range/sortOrder; everything untouched keeps
// its safe hardcoded default. A CMS entry for a genuinely new id adds a new band
// on top — that's the one case this doesn't structurally protect against, matching
// generateMathsQuestion's own existing fallback-to-Builder for any band id outside
// GENERATORS.
//
// One consequence, also deliberate: retiring one of the ORIGINAL five bands via the
// CMS does NOT remove it — cmsMathsBands() excludes retired entries from what it
// returns, so a retired original band simply has no override and falls straight back
// to its hardcoded default, exactly as if it had never been touched. Removing one of
// the five structural bands entirely is a code change (edit MATHS_BAND_ORDER), not a
// CMS action — consistent with "the CMS never changes what's structurally guaranteed
// to exist." Retire is fully meaningful for any NEW band added beyond the original
// five, where no such guarantee applies.
export function effectiveMathsBands() {
  const cmsBands = cmsMathsBands();
  const cmsById = new Map((cmsBands || []).map((b) => [b.id, b]));
  const merged = {};
  MATHS_BAND_ORDER.forEach((id, i) => {
    merged[id] = { ...MATHS_BANDS[id], sortOrder: i, years: [] }; // MATHS_BAND_ORDER's position IS the default sortOrder — MATHS_BANDS itself carries no explicit order field. years deliberately empty in code: year claims arrive ONLY via CMS (ladder.js rollout note).
  });
  for (const [id, b] of cmsById) {
    merged[id] = { label: b.label, blurb: b.blurb, minAge: b.minAge ?? 0, maxAge: b.maxAge ?? 99, sortOrder: b.sortOrder, years: sanitizeYears(b.years) };
  }
  return merged;
}

export function effectiveMathsBandOrder() {
  const bands = effectiveMathsBands();
  return Object.keys(bands).sort((a, b) => bands[a].sortOrder - bands[b].sortOrder);
}

// Picks a band for a child: an explicit parent-chosen band always wins, otherwise falls
// back to age. Ages outside the configured ranges clamp to the nearest end (youngest band
// below Foundations, oldest band above Genius) rather than a hardcoded default, so this
// keeps working correctly if bands are added, removed, or reordered later.
export function resolveMathsBand(child, override) {
  const bands = effectiveMathsBands();
  const order = effectiveMathsBandOrder();
  if (override && override !== "auto" && bands[override]) return override;
  // Difficulty Ladder (docs/DIFFICULTY_LADDER_SCOPE.md): school year beats age when
  // year-keyed rungs exist — resolveSchoolYear is parent-confirmed year first, live
  // estimate otherwise, which is what separates a young 6 (Y1) from an old 7 (Y3).
  // Returns null (→ the age path below, unchanged) until ladder content is imported,
  // for a child with no resolvable year, or for a year sitting in a content gap.
  const byYear = resolveRungByYear(bands, order, resolveSchoolYear(child));
  if (byYear) return byYear;
  const age = effectiveAge(child);
  if (age === null) return order.includes("builder") ? "builder" : order[0] || "builder";
  const found = order.find((k) => bands[k] && age >= bands[k].minAge && age <= bands[k].maxAge);
  if (found) return found;
  if (!order.length) return "builder";
  return age < (bands[order[0]]?.minAge ?? 0) ? order[0] : order[order.length - 1];
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[randInt(0, arr.length - 1)];
}

/* ------------------------------ session dedup -------------------------------
 * Both games generate each question independently, so a small generator set (or a single
 * TT table picked) could draw the exact same prompt twice in one test — reads as broken to
 * a child even though each individual draw was fair (Alan, Aug 2026 staging feedback).
 * buildUniqueSession retries a draw that exactly repeats an EARLIER prompt in the same
 * session, up to RETRY_LIMIT times, then accepts the repeat rather than looping forever on
 * a generator whose answer space is smaller than the session (e.g. TT Streaks with a single
 * table selected only has 12 distinct facts).
 * ---------------------------------------------------------------------------- */

const SESSION_DEDUP_RETRY_LIMIT = 25;

// Exported for reuse by the CMS's sample-question preview (handleMathsSample in
// worker/handlers/cms-handlers.js) — real incident, Aug 2026: the preview originally
// called generateFromOverrides in a bare loop with no dedup at all, while every REAL
// study session goes through this exact function via generateMathsSession. That gap
// made the preview show MORE repetition than the app ever actually would, and looked
// like a genuine regression of a repetition fix from a previous session when it was
// actually just the preview tool being unrepresentative of real behaviour. Reusing
// this function directly means the preview can never drift from what a real session
// looks like again — one dedup implementation, not two.
export function buildUniqueSession(size, draw) {
  const used = new Set();
  const out = [];
  for (let i = 0; i < size; i++) {
    let q = draw();
    let attempts = 0;
    while (used.has(q.prompt) && attempts < SESSION_DEDUP_RETRY_LIMIT) {
      q = draw();
      attempts++;
    }
    used.add(q.prompt);
    out.push(q);
  }
  return out;
}

export function generateTTQuestion(tables) {
  const list = tables && tables.length > 0 ? tables : DEFAULT_TT_TABLES;
  const table = pick(list);
  const multiplier = randInt(1, 12);
  const flip = Math.random() < 0.5;
  const a = flip ? table : multiplier;
  const b = flip ? multiplier : table;
  return { prompt: `${a} \u00d7 ${b}`, answer: table * multiplier, table, grade: TT_TABLE_GRADE[table] || 3, topic: `table-${table}` };
}
// Adaptive (LB-19 §3.5): tables graded 2,5,10 → 1 · 3,4 → 2 · 6,9 → 3 · 7,8 → 4 · 11,12 → 5.
export const TT_TABLE_GRADE = { 1: 1, 2: 1, 5: 1, 10: 1, 3: 2, 4: 2, 6: 3, 9: 3, 7: 4, 8: 4, 11: 5, 12: 5 };

// The tables that sit at each grade, as a phrase. Used instead of the level WORD wherever
// times tables show a level: "the 6s and 9s" says what they are practising, where
// "Champion" claims something they have not done yet.
export const TT_GRADE_TABLES = { 1: [2, 5, 10], 2: [3, 4], 3: [6, 9], 4: [7, 8], 5: [11, 12] };
export function ttLevelPhrase(target) {
  const list = TT_GRADE_TABLES[Math.max(1, Math.min(5, Math.round(target || 1)))] || [];
  if (list.length === 0) return "";
  const names = list.map((n) => `${n}s`);
  return `the ${names.slice(0, -1).join(", ")}${names.length > 1 ? " and " : ""}${names[names.length - 1]}`;
}
export function generateAdaptiveTTSession(tables, size, adaptiveState, params) {
  const list = tables && tables.length > 0 ? tables : DEFAULT_TT_TABLES;
  if (!adaptiveState) return generateTTSession(list, size);
  const target = Math.max(1, Math.min(5, adaptiveState.target || 1));
  const P = params || { draw: { at: 60, below: 20, above: 20 }, retention: 10 };
  const mastered = (t) => !!(adaptiveState.topics && adaptiveState.topics[`table-${t}`] && adaptiveState.topics[`table-${t}`].mastered);
  // (table, level): level 1 = multipliers 1–5, level 2 = 1–12, level 3 = 6–12 flipped.
  const pairs = [];
  for (const t of list) for (const level of [1, 2, 3]) {
    const g = Math.max(1, Math.min(5, (TT_TABLE_GRADE[t] || 3) + (level - 2)));
    let w = g === target ? P.draw.at : g === target - 1 ? P.draw.below : g === target + 1 ? P.draw.above : 0;
    if (mastered(t)) w = level === 3 ? Math.round(P.retention) : 0;
    if (w > 0) pairs.push({ t, level, w, g });
  }
  if (!pairs.length) return generateTTSession(list, size);
  const pickPair = () => { let r = Math.random() * pairs.reduce((a, p) => a + p.w, 0); for (const p of pairs) { r -= p.w; if (r <= 0) return p; } return pairs[pairs.length - 1]; };
  return buildUniqueSession(size, () => { const p = pickPair(); const m = p.level === 1 ? randInt(1, 5) : p.level === 3 ? randInt(6, 12) : randInt(1, 12); const flip = p.level === 3 ? true : Math.random() < 0.5; const a = flip ? m : p.t; const b = flip ? p.t : m; return { prompt: `${a} \u00d7 ${b}`, answer: p.t * m, table: p.t, level: p.level, grade: p.g, topic: `table-${p.t}` }; });
}

// ---------------------------------------------------------------------------
// Parameterized question generation (docs/MATHS_PARAMETERIZATION_SCOPE.md).
// Replaces the old flat GENERATORS array-of-closures with named, parameterized
// types — every formula below is byte-identical to what it replaced; only the
// literal numbers moved from inline code to `params`. Validated via a sampling
// comparison against the original (2,000 questions/band, ±3pp tolerance on every
// type's proportion, exact answer-range convergence at 100k+ samples) before this
// replacement was made — see the baseline-capture tooling delivered alongside
// docs/MATHS_PARAMETERIZATION_SCOPE.md.
//
// CMS override, per-key, never whole-collection (same reasoning as
// effectiveMathsBands): a band's type-mix, and each type's own params/slot-items,
// are looked up independently. Publishing one band's weights or one type's params
// never touches anything else. With nothing published, every default below
// reproduces today's exact behaviour — confirmed by the validation above, not
// assumed.

const QUALITATIVE_WEIGHT = { none: 0, low: 1, medium: 2, high: 3 };

// Weighted pick over items carrying a numeric .weight (0 = excluded entirely).
// Falls back to a plain uniform pick if every item's weight resolves to 0 — an
// all-"none" set shouldn't silently generate nothing; see effectiveTypeMix's
// comment for why this matters for a band with a bad CMS config specifically.
function weightedPick(items) {
  const usable = items.filter((i) => i.weight > 0);
  const pool = usable.length ? usable : items;
  const total = pool.reduce((sum, i) => sum + Math.max(i.weight, 0.0001), 0);
  let r = Math.random() * total;
  for (const item of pool) {
    const w = Math.max(item.weight, 0.0001);
    if (r < w) return item;
    r -= w;
  }
  return pool[pool.length - 1];
}

// TYPES: one entry per named question type. `band` groups it for type-mix
// purposes; `defaultParams` are the exact literal values from the original
// generator; `defaultSlotItems` only exists for the five types with a discrete,
// weightable item set (docs/MATHS_PARAMETERIZATION_SCOPE.md §2.3). `generate(p,
// slotItems)` is the untouched original formula, reading from `p`/`slotItems`
// instead of closed-over literals.
const TYPES = {
  addition_basic: {
    band: "foundations",
    grade: 1,
    defaultParams: { aMin: 1, aMax: 15, sumMax: 20 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(1, p.sumMax - a);
      return { prompt: `${a} + ${b}`, answer: a + b };
    },
  },
  subtraction_basic: {
    band: "foundations",
    grade: 2,
    defaultParams: { aMin: 5, aMax: 20 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(1, a);
      return { prompt: `${a} - ${b}`, answer: a - b };
    },
  },

  // ------------------------- Difficulty Ladder types (docs/DIFFICULTY_LADDER_SCOPE.md) -------------------------
  // Homed under the NEW rung ids ("sprout", "climber") on purpose — see the
  // TYPE_IDS_BY_BAND comment below: homing them under foundations/builder would add
  // them to live bands' baselines on deploy day. They only ever reach a child once
  // the ladder's CMS bands exist and resolution routes someone to those rungs.
  // Params WERE global-per-type when these were written (maths-type-params keyed by typeId
  // alone), which is why they exist as separate types rather than easier params on
  // addition_basic. Since Maths Ladder v2 params resolve band-first ("<bandId>:<typeId>",
  // content.js), so a rung can now retune a shared type without touching another rung.
  counting_next: {
    band: "sprout",
    grade: 1,
    defaultParams: { min: 1, max: 20 },
    generate(p) {
      const n = randInt(p.min + 1, p.max - 1);
      const after = Math.random() < 0.5;
      return { prompt: after ? `What comes after ${n}?` : `What comes before ${n}?`, answer: after ? n + 1 : n - 1 };
    },
  },
  addition_to_10: {
    band: "sprout",
    grade: 2,
    defaultParams: { aMin: 1, aMax: 9, sumMax: 10 },
    generate(p) {
      const a = randInt(p.aMin, Math.min(p.aMax, p.sumMax - 1));
      const b = randInt(1, p.sumMax - a);
      return { prompt: `${a} + ${b}`, answer: a + b };
    },
  },
  subtraction_to_10: {
    band: "sprout",
    grade: 3,
    defaultParams: { aMin: 2, aMax: 10 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(1, a);
      return { prompt: `${a} - ${b}`, answer: a - b };
    },
  },
  number_bonds_10: {
    band: "sprout",
    grade: 2,
    defaultParams: { target: 10 },
    generate(p) {
      const b = randInt(1, p.target - 1);
      return { prompt: `? + ${b} = ${p.target}`, answer: p.target - b };
    },
  },
  doubles: {
    band: "sprout",
    grade: 4,
    defaultParams: { min: 1, max: 10 },
    generate(p) {
      const n = randInt(p.min, p.max);
      return { prompt: `Double ${n}?`, answer: n * 2 };
    },
  },
  number_bonds_20: {
    band: "climber",
    grade: 1,
    defaultParams: { target: 20 },
    generate(p) {
      const b = randInt(1, p.target - 1);
      return { prompt: `? + ${b} = ${p.target}`, answer: p.target - b };
    },
  },
  times_table_easy: {
    band: "climber",
    grade: 3,
    defaultParams: { multiplierMin: 1, multiplierMax: 12 },
    // Y2's own tables only (2/5/10) with its own GLOBAL slot items — restricting
    // times_table_weighted's items instead would have re-tuned Builder for everyone.
    defaultSlotItems: [2, 5, 10].map((t) => ({ id: String(t), label: "×" + t, weight: QUALITATIVE_WEIGHT.medium })),
    generate(p, slotItems) {
      const table = parseInt(weightedPick(slotItems).id, 10);
      const multiplier = randInt(p.multiplierMin, p.multiplierMax);
      const flip = Math.random() < 0.5;
      const a = flip ? table : multiplier;
      const b = flip ? multiplier : table;
      return { prompt: `${a} × ${b}`, answer: table * multiplier };
    },
  },

  addition: {
    band: "builder",
    grade: 1,
    defaultParams: { aMin: 10, aMax: 80, sumMax: 100 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(1, p.sumMax - a);
      return { prompt: `${a} + ${b}`, answer: a + b };
    },
  },
  subtraction: {
    band: "builder",
    grade: 1,
    defaultParams: { aMin: 10, aMax: 100 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(1, a);
      return { prompt: `${a} - ${b}`, answer: a - b };
    },
  },
  times_table_weighted: {
    band: "builder",
    grade: 2,
    defaultParams: { multiplierMin: 2, multiplierMax: 12 },
    // The 11 tables 2–12, each individually weighted. Defaults: the original 8
    // "harder" tables = High(3), the original 3 "easy" tables (2,5,10) = Medium(2)
    // — NOT Low. 8×3 / (8×3+3×2) = 24/30 = 80%, exactly matching the original
    // 80/20 split (computed directly before writing this, then confirmed against
    // the sampling comparison — a naive High/Low default would have been off by
    // ~9pp, well outside tolerance).
    defaultSlotItems: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((t) => ({
      id: String(t), label: "×" + t, weight: [3, 4, 6, 7, 8, 9, 11, 12].includes(t) ? QUALITATIVE_WEIGHT.high : QUALITATIVE_WEIGHT.medium,
    })),
    generate(p, slotItems) {
      const table = parseInt(weightedPick(slotItems).id, 10);
      const multiplier = randInt(p.multiplierMin, p.multiplierMax);
      const flip = Math.random() < 0.5;
      const a = flip ? table : multiplier;
      const b = flip ? multiplier : table;
      return { prompt: `${a} × ${b}`, answer: table * multiplier };
    },
  },
  division_basic: {
    band: "builder",
    grade: 3,
    defaultParams: { bMin: 2, bMax: 12, ansMin: 2, ansMax: 12 },
    generate(p) {
      const b = randInt(p.bMin, p.bMax);
      const ans = randInt(p.ansMin, p.ansMax);
      return { prompt: `${b * ans} ÷ ${b}`, answer: ans };
    },
  },
  division_harder: {
    band: "builder",
    grade: 5,
    defaultParams: { bMin: 2, bMax: 10, ansMin: 10, ansMax: 20 },
    generate(p) {
      const b = randInt(p.bMin, p.bMax);
      const ans = randInt(p.ansMin, p.ansMax);
      return { prompt: `${b * ans} ÷ ${b}`, answer: ans };
    },
  },
  shape_sides: {
    band: "builder",
    grade: 2,
    defaultParams: {},
    defaultSlotItems: [
      { id: "triangle", label: "triangle", sides: 3, weight: QUALITATIVE_WEIGHT.medium },
      { id: "square", label: "square", sides: 4, weight: QUALITATIVE_WEIGHT.medium },
      { id: "rectangle", label: "rectangle", sides: 4, weight: QUALITATIVE_WEIGHT.medium },
      { id: "pentagon", label: "pentagon", sides: 5, weight: QUALITATIVE_WEIGHT.medium },
      { id: "hexagon", label: "hexagon", sides: 6, weight: QUALITATIVE_WEIGHT.medium },
      { id: "octagon", label: "octagon", sides: 8, weight: QUALITATIVE_WEIGHT.medium },
    ],
    generate(p, slotItems) {
      const s = weightedPick(slotItems);
      return { prompt: `How many sides does a ${s.label} have?`, answer: s.sides };
    },
  },
  change_from_spending: {
    band: "builder",
    grade: 3,
    defaultParams: { priceMin: 1, priceMax: 8, notes: [10, 20] },
    generate(p) {
      const price = randInt(p.priceMin, p.priceMax);
      const paid = pick(p.notes.filter((n) => n > price));
      return { prompt: `You have £${paid}. You spend £${price}. How much change do you get?`, answer: paid - price };
    },
  },

  multiplication: {
    band: "challenger",
    grade: 1,
    defaultParams: { aMin: 11, aMax: 25, bMin: 2, bMax: 9 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(p.bMin, p.bMax);
      return { prompt: `${a} × ${b}`, answer: a * b };
    },
  },
  division: {
    band: "challenger",
    grade: 1,
    defaultParams: { bMin: 2, bMax: 12, ansMin: 5, ansMax: 20 },
    generate(p) {
      const b = randInt(p.bMin, p.bMax);
      const ans = randInt(p.ansMin, p.ansMax);
      return { prompt: `${b * ans} ÷ ${b}`, answer: ans };
    },
  },
  fraction_of_amount: {
    band: "challenger",
    grade: 2,
    defaultParams: { denominators: [2, 3, 4, 5, 10], amountMultMin: 2, amountMultMax: 12 },
    generate(p) {
      const den = pick(p.denominators);
      const num = randInt(1, den - 1);
      const amount = den * randInt(p.amountMultMin, p.amountMultMax);
      return { prompt: `${num}/${den} of ${amount}`, answer: (amount / den) * num };
    },
  },
  percent_of_amount_challenger: {
    band: "challenger",
    grade: 2,
    defaultParams: { percents: [10, 20, 25, 50], amounts: [20, 40, 60, 80, 100, 120] },
    generate(p) {
      const pct = pick(p.percents);
      const amount = pick(p.amounts);
      return { prompt: `${pct}% of ${amount}`, answer: (amount * pct) / 100 };
    },
  },
  equal_share_word: {
    band: "challenger",
    grade: 3,
    defaultParams: { perShareMin: 3, perShareMax: 12, partsMin: 2, partsMax: 8 },
    defaultSlotItems: ["bananas", "sweets", "marbles", "stickers", "grapes", "cookies", "pencils", "conkers"].map((o) => ({ id: o, label: o, weight: QUALITATIVE_WEIGHT.medium })),
    generate(p, slotItems) {
      const perShare = randInt(p.perShareMin, p.perShareMax);
      const parts = randInt(p.partsMin, p.partsMax);
      const total = perShare * parts;
      const obj = weightedPick(slotItems).label;
      return { prompt: `Share ${total} ${obj} equally between ${parts}. How many does each get?`, answer: perShare };
    },
  },
  cost_of_items_word: {
    band: "challenger",
    grade: 4,
    defaultParams: { priceMin: 2, priceMax: 9, qtyMin: 3, qtyMax: 9 },
    defaultSlotItems: ["pencils", "stickers", "apples", "notebooks", "balloons"].map((o) => ({ id: o, label: o, weight: QUALITATIVE_WEIGHT.medium })),
    generate(p, slotItems) {
      const price = randInt(p.priceMin, p.priceMax);
      const qty = randInt(p.qtyMin, p.qtyMax);
      const obj = weightedPick(slotItems).label;
      return { prompt: `${obj} cost £${price} each. How much do ${qty} ${obj} cost?`.replace(/^\w/, (c) => c.toUpperCase()), answer: price * qty };
    },
  },
  order_of_ops_basic: {
    band: "challenger",
    grade: 4,
    defaultParams: { abMin: 2, abMax: 6, cMin: 1, cMax: 10 },
    generate(p) {
      const a = randInt(p.abMin, p.abMax);
      const b = randInt(p.abMin, p.abMax);
      const c = randInt(p.cMin, p.cMax);
      return { prompt: `${a} × ${b} + ${c}`, answer: a * b + c };
    },
  },
  unit_conversion: {
    band: "challenger",
    grade: 5,
    defaultParams: { amountMin: 2, amountMax: 12 },
    defaultSlotItems: [
      { id: "m_cm", label: "m_cm", from: "m", to: "cm", factor: 100, weight: QUALITATIVE_WEIGHT.medium },
      { id: "kg_g", label: "kg_g", from: "kg", to: "g", factor: 1000, weight: QUALITATIVE_WEIGHT.medium },
      { id: "km_m", label: "km_m", from: "km", to: "m", factor: 1000, weight: QUALITATIVE_WEIGHT.medium },
      { id: "hr_min", label: "hr_min", from: "hours", to: "minutes", factor: 60, weight: QUALITATIVE_WEIGHT.medium },
      { id: "min_sec", label: "min_sec", from: "minutes", to: "seconds", factor: 60, weight: QUALITATIVE_WEIGHT.medium },
    ],
    generate(p, slotItems) {
      const u = weightedPick(slotItems);
      const amount = randInt(p.amountMin, p.amountMax);
      return { prompt: `${amount} ${u.from} is how many ${u.to}?`, answer: amount * u.factor };
    },
  },

  decimal_add_sub: {
    band: "strategist",
    grade: 1,
    // allowNegative (Maths Ladder v2): true keeps the original behaviour (a − b may go below
    // zero); false swaps the operands so a subtraction never does — the Y5/Y6 rungs set it
    // false, since negative decimals are secondary-school content.
    defaultParams: { rawMin: 10, rawMax: 200, allowNegative: true },
    generate(p) {
      let a = randInt(p.rawMin, p.rawMax) / 10;
      let b = randInt(p.rawMin, p.rawMax) / 10;
      const op = Math.random() < 0.5 ? "+" : "-";
      if (op === "-" && p.allowNegative === false && b > a) [a, b] = [b, a];
      const answer = op === "+" ? a + b : a - b;
      return { prompt: `${a.toFixed(1)} ${op} ${b.toFixed(1)}`, answer: Math.round(answer * 10) / 10 };
    },
  },
  percent_of_amount_strategist: {
    band: "strategist",
    grade: 2,
    defaultParams: { percents: [5, 15, 20, 30, 40, 60, 75], amounts: [20, 40, 60, 80, 120, 200] },
    generate(p) {
      const pct = pick(p.percents);
      const amount = pick(p.amounts);
      return { prompt: `${pct}% of ${amount}`, answer: (amount * pct) / 100 };
    },
  },
  negative_add_sub: {
    band: "strategist",
    grade: 3,
    defaultParams: { aMin: -20, aMax: 20, bMin: -20, bMax: 20 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(p.bMin, p.bMax);
      const op = Math.random() < 0.5 ? "+" : "-";
      return { prompt: `${a} ${op} (${b})`, answer: op === "+" ? a + b : a - b };
    },
  },
  order_of_ops: {
    band: "strategist",
    grade: 2,
    defaultParams: { abcMin: 2, abcMax: 9 },
    generate(p) {
      const a = randInt(p.abcMin, p.abcMax);
      const b = randInt(p.abcMin, p.abcMax);
      const c = randInt(p.abcMin, p.abcMax);
      return { prompt: `${a} + ${b} × ${c}`, answer: a + b * c };
    },
  },
  ratio_share: {
    band: "strategist",
    grade: 4,
    defaultParams: { perPartMin: 2, perPartMax: 10, ratioMin: 2, ratioMax: 6 },
    generate(p) {
      const perPart = randInt(p.perPartMin, p.perPartMax);
      let a = randInt(p.ratioMin, p.ratioMax);
      let b = randInt(p.ratioMin, p.ratioMax);
      while (b === a) b = randInt(p.ratioMin, p.ratioMax);
      const total = perPart * (a + b);
      const smaller = perPart * Math.min(a, b);
      const larger = perPart * Math.max(a, b);
      const askSmaller = Math.random() < 0.5;
      return { prompt: `Share £${total} in the ratio ${a}:${b}. What's the ${askSmaller ? "smaller" : "larger"} share?`, answer: askSmaller ? smaller : larger };
    },
  },
  percent_discount: {
    band: "strategist",
    grade: 4,
    defaultParams: { originals: [20, 40, 60, 80, 100, 120, 200], percents: [10, 15, 20, 25] },
    generate(p) {
      const original = pick(p.originals);
      const pct = pick(p.percents);
      const discounted = original - (original * pct) / 100;
      return { prompt: `A jacket costs £${original}. It's reduced by ${pct}%. What's the new price?`, answer: discounted };
    },
  },
  unit_rate: {
    band: "strategist",
    grade: 5,
    defaultParams: { perHourMin: 20, perHourMax: 60, hours1Min: 2, hours1Max: 4, hours2Min: 5, hours2Max: 8 },
    generate(p) {
      const perHour = randInt(p.perHourMin, p.perHourMax);
      const hours1 = randInt(p.hours1Min, p.hours1Max);
      const hours2 = randInt(p.hours2Min, p.hours2Max);
      const dist1 = perHour * hours1;
      return { prompt: `A car travels ${dist1} miles in ${hours1} hours at a steady speed. How far does it travel in ${hours2} hours?`, answer: perHour * hours2 };
    },
  },
  money_change: {
    band: "strategist",
    grade: 5,
    defaultParams: { priceWholeMin: 2, priceWholeMax: 8, priceP: [0, 25, 50, 75], qtyMin: 2, qtyMax: 4, notes: [10, 20, 50] },
    generate(p) {
      const priceWhole = randInt(p.priceWholeMin, p.priceWholeMax);
      const priceP = pick(p.priceP);
      const price = priceWhole + priceP / 100;
      const qty = randInt(p.qtyMin, p.qtyMax);
      const total = Math.round(price * qty * 100) / 100;
      const notes = p.notes.filter((n) => n > total);
      const paid = notes[0] || 50;
      const change = Math.round((paid - total) * 100) / 100;
      return { prompt: `Jamie buys ${qty} books at £${price.toFixed(2)} each and pays with a £${paid} note. How much change?`, answer: change };
    },
  },

  // ------------------------- Maths Ladder v2 types (docs/MATHS_LADDER_V2_SPEC.md) -------------------------
  // Homed under the pseudo-band "ladder" for the same reason the Difficulty Ladder types
  // are homed under sprout/climber: nothing already live gains a type on deploy day. They
  // reach a child only through a band's CMS type-mix (cms-seed/difficulty-ladder-v2.json).
  multiply_10_100: {
    band: "ladder",
    grade: 1,
    // Y4 "multiply by 10 and 100"; Y5 adds 1000. multipliers is the list to pick from.
    defaultParams: { aMin: 2, aMax: 99, multipliers: [10, 100] },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const m = pick(p.multipliers);
      const flip = Math.random() < 0.5;
      return { prompt: flip ? `${a} × ${m}` : `${m} × ${a}`, answer: a * m };
    },
  },
  missing_factor: {
    band: "ladder",
    grade: 2,
    // "? × 6 = 42" — a times-table fact asked backwards; the same items shape as
    // times_table_weighted so a band can say which tables are in play.
    defaultParams: { multiplierMin: 2, multiplierMax: 12 },
    defaultSlotItems: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((t) => ({ id: String(t), label: "×" + t, weight: QUALITATIVE_WEIGHT.medium })),
    generate(p, slotItems) {
      const table = parseInt(weightedPick(slotItems).id, 10);
      const multiplier = randInt(p.multiplierMin, p.multiplierMax);
      const flip = Math.random() < 0.5;
      return flip ? { prompt: `? × ${table} = ${table * multiplier}`, answer: multiplier } : { prompt: `${table} × ? = ${table * multiplier}`, answer: multiplier };
    },
  },
  rounding: {
    band: "ladder",
    grade: 2,
    // Y4 rounds to the nearest 10, 100 and 1000; nearestOptions is the list to pick from,
    // and the number is always at least one place longer than the rounding unit.
    defaultParams: { nMin: 11, nMax: 999, nearestOptions: [10, 100] },
    generate(p) {
      const unit = pick(p.nearestOptions);
      const n = randInt(Math.max(p.nMin, unit + 1), Math.max(p.nMax, unit * 2));
      const answer = Math.round(n / unit) * unit;
      return { prompt: `Round ${n} to the nearest ${unit}`, answer };
    },
  },

  linear_eq_add: {
    band: "genius",
    grade: 1,
    defaultParams: { xMin: 2, xMax: 12, aMin: 2, aMax: 9, bMin: 1, bMax: 20 },
    generate(p) {
      const x = randInt(p.xMin, p.xMax);
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(p.bMin, p.bMax);
      const c = a * x + b;
      return { prompt: `${a}x + ${b} = ${c}. What is x?`, answer: x };
    },
  },
  linear_eq_sub: {
    band: "genius",
    grade: 1,
    defaultParams: { xMin: 2, xMax: 12, aMin: 2, aMax: 9, bMin: 1, bMax: 15 },
    generate(p) {
      const x = randInt(p.xMin, p.xMax);
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(p.bMin, p.bMax);
      const c = a * x - b;
      return { prompt: `${a}x - ${b} = ${c}. What is x?`, answer: x };
    },
  },
  linear_eq_div: {
    band: "genius",
    grade: 2,
    defaultParams: { aMin: 2, aMax: 9, multMin: 2, multMax: 12 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const x = a * randInt(p.multMin, p.multMax);
      return { prompt: `x/${a} = ${x / a}. What is x?`, answer: x };
    },
  },
  substitution: {
    band: "genius",
    grade: 2,
    defaultParams: { abMin: 2, abMax: 9, coefMin: 2, coefMax: 5 },
    generate(p) {
      const a = randInt(p.abMin, p.abMax);
      const b = randInt(p.abMin, p.abMax);
      const coefA = randInt(p.coefMin, p.coefMax);
      const coefB = randInt(p.coefMin, p.coefMax);
      return { prompt: `If a = ${a} and b = ${b}, what is ${coefA}a + ${coefB}b?`, answer: coefA * a + coefB * b };
    },
  },
  squares_roots: {
    band: "genius",
    grade: 3,
    defaultParams: { squareMin: 4, squareMax: 20, rootMin: 4, rootMax: 16 },
    generate(p) {
      if (Math.random() < 0.5) {
        const n = randInt(p.squareMin, p.squareMax);
        return { prompt: `What is ${n}²?`, answer: n * n };
      }
      const root = randInt(p.rootMin, p.rootMax);
      return { prompt: `What is the square root of ${root * root}?`, answer: root };
    },
  },
  successive_percent: {
    band: "genius",
    grade: 4,
    defaultParams: { originals: [40, 60, 80, 100, 120, 200], pct1Options: [10, 20, 25], pct2Options: [10, 20] },
    generate(p) {
      const original = pick(p.originals);
      const pct1 = pick(p.pct1Options);
      const pct2 = pick(p.pct2Options);
      const afterFirst = original - (original * pct1) / 100;
      const final = Math.round((afterFirst - (afterFirst * pct2) / 100) * 100) / 100;
      return { prompt: `A jumper costs £${original}. It's reduced by ${pct1}%, then a further ${pct2}% off the new price. What's the final price?`, answer: final };
    },
  },
  order_of_ops_brackets: {
    band: "genius",
    grade: 4,
    defaultParams: { aMin: 3, aMax: 12, bcMin: 2, bcMax: 9, dMin: 1, dMax: 10 },
    generate(p) {
      const a = randInt(p.aMin, p.aMax);
      const b = randInt(p.bcMin, p.bcMax);
      const c = randInt(p.bcMin, p.bcMax);
      const d = randInt(p.dMin, p.dMax);
      return { prompt: `(${a} + ${b}) × ${c} - ${d}`, answer: (a + b) * c - d };
    },
  },
  remainder: {
    band: "genius",
    grade: 3,
    defaultParams: { divisorMin: 4, divisorMax: 9, quotientMin: 3, quotientMax: 10 },
    generate(p) {
      const divisor = randInt(p.divisorMin, p.divisorMax);
      const quotient = randInt(p.quotientMin, p.quotientMax);
      const remainder = randInt(1, divisor - 1);
      const dividend = divisor * quotient + remainder;
      return { prompt: `What is the remainder when ${dividend} is divided by ${divisor}?`, answer: remainder };
    },
  },
};

const TYPE_IDS_BY_BAND = { foundations: [], builder: [], challenger: [], strategist: [], genius: [] };
// (TYPE_IDS_BY_BAND[def.band] ||= []) rather than a fixed-key push: ladder types
// (docs/DIFFICULTY_LADDER_SCOPE.md) are homed under NEW band ids ("sprout",
// "climber") so the structural five bands' curated baselines stay byte-identical —
// homing them under foundations/builder would have silently added new question
// types to live bands on deploy day, before any content was approved.
for (const [typeId, def] of Object.entries(TYPES)) (TYPE_IDS_BY_BAND[def.band] ||= []).push(typeId);

// Plain-data projection of TYPES for anything outside this module that needs to know
// the type registry WITHOUT the generator functions themselves (which aren't
// serializable and shouldn't be exposed as data anyway) — specifically the CMS's
// worker-side registry endpoint, so the Maths workout editor always reflects
// whatever's actually in this file with zero risk of a separately-maintained copy
// drifting out of sync (the exact class of bug that's bitten this project before —
// see the batch-size-unification fix earlier today).
export const MATHS_TYPE_REGISTRY = Object.fromEntries(
  Object.entries(TYPES).map(([typeId, def]) => [typeId, { band: def.band, grade: def.grade || 3, defaultParams: def.defaultParams, defaultSlotItems: def.defaultSlotItems || null }])
);
export const MATHS_TYPE_IDS_BY_BAND = TYPE_IDS_BY_BAND;

// Maths Ladder v2: the EFFECTIVE type set of a band — its registered baseline UNIONED with
// whatever its published type-mix names (registered ids only). generateFromOverrides has
// always drawn from this union; the Coach did not (it read TYPE_IDS_BY_BAND alone), which
// is why the types the Difficulty Ladder added through the CMS — Climber's add/subtract,
// Foundations' bonds, all of Navigator — were invisible to it: Coach-off showed them, Coach-on
// did not, and Navigator (baseline empty) fell back to a plain session with a target that
// moved nothing. Every Coach read of "the band's types" goes through here now: the session
// draw, topicsInBand for the record path (App.jsx), the parent row, the hub's upcoming list
// and "coming up next".
export function mathsBandTypeIds(bandId) {
  const base = TYPE_IDS_BY_BAND[bandId] || [];
  const mix = cmsMathsTypeMix(bandId) || {};
  const fromMix = Object.keys(mix).filter((id) => TYPES[id]);
  // A type the band's mix has switched off ("none") is out of the pool, baseline or not —
  // the Coach's record path would otherwise count it as a topic the child must master
  // before the band can move up, for a type they are never asked.
  return [...new Set([...base, ...fromMix])].filter((id) => mix[id] !== "none");
}

// How far level 3 stretches a numeric range (Maths Ladder v2): 1.5 means the top of every
// range moves out by half the range (and sumMax/target-style caps by ×1.5). Was a fixed ×2
// — which is what put 175 − 89 and 238 ÷ 17 into Builder, a Year 3 band. Per band and
// type via the type-mix entry's `l3` map (cmsMathsTypeL3).
export const DEFAULT_L3_FACTOR = 1.5;
export function l3FactorFor(typeId, bandId) {
  const m = bandId ? cmsMathsTypeL3(bandId) : null;
  const f = m && typeof m[typeId] === "number" ? m[typeId] : null;
  return f && f >= 1 && f <= 4 ? f : DEFAULT_L3_FACTOR;
}

// Plain-data projection of the five ORIGINAL structural bands (MATHS_BANDS +
// MATHS_BAND_ORDER), for the CMS's band list to synthesize a visible, selectable row
// for each one even when no explicit maths-band CMS entry exists yet — real
// incident, Aug 2026: without this, the Maths workout tab only ever showed bands
// that had already been explicitly created, so the five bands that ALREADY exist
// and work in the live app (via effectiveMathsBands' per-band fallback) were
// invisible in the CMS entirely. An admin trying to view/tweak the real Builder
// couldn't find it, and ended up creating unrelated custom bands with made-up ids
// that had no question types (a custom band only gets
// types via its type-mix entry — see generateFromOverrides — and, since the
// Difficulty Ladder, the new-rung ids sprout/climber also carry their own
// registered baselines). sortOrder mirrors each
// band's position in MATHS_BAND_ORDER, the same convention effectiveMathsBands()
// uses internally.
export const MATHS_BAND_DEFAULTS = Object.fromEntries(
  MATHS_BAND_ORDER.map((id, i) => [id, { ...MATHS_BANDS[id], sortOrder: i }])
);

// Core generation logic, taking overrides EXPLICITLY rather than looking them up via
// cmsMathsTypeMix/cmsMathsTypeParams/cmsMathsSlotItems — those three depend on
// currentBundle()/localStorage, which only exist in a browser. This function has no
// such dependency, so it works identically in the live app (called with overrides
// built from the CMS lookups, see generateMathsQuestion below) and in a Cloudflare
// Worker (called with DRAFT overrides fetched directly from KV, for the "Generate
// sample questions" preview — see worker/handlers/cms-handlers.js). One code path,
// not two, so a preview can never drift from what the real app would generate.
//
// overrides: { typeMix: { typeId: "high"|"medium"|"low"|"none" }, typeParams:
// { typeId: {...} }, slotItems: { typeId: [...] } } — every key optional; a missing
// type in any of these just uses that type's own hardcoded default.
export function generateFromOverrides(bandId, overrides) {
  const typeMixOverride = (overrides && overrides.typeMix) || {};
  const typeParamsOverride = (overrides && overrides.typeParams) || {};
  const slotItemsOverride = (overrides && overrides.slotItems) || {};

  // Which types are in scope for this band: the band's own registered set
  // (TYPE_IDS_BY_BAND — unchanged for the five original bands, so their validated
  // default behaviour never moves) UNIONED with whatever type ids actually appear as
  // keys in the type-mix override. This is what lets a custom band beyond the
  // original five have real question types at all (Alan, Aug 2026: "I can't add
  // questions to a band that I have created") — a custom band has no baseline set of
  // its own, so its "union" is simply whatever types its type-mix override assigns
  // it, drawn from the full pool of 33 rather than needing brand-new
  // question-writing code. For an ORIGINAL band, this only ever ADDS to its curated
  // default set (an admin opting a type in from elsewhere) — it can never silently
  // drop one of the five bands' own designed-for types, since those are always in
  // the baseline regardless of what the override does or doesn't mention.
  const baselineTypeIds = TYPE_IDS_BY_BAND[bandId] || [];
  const overrideTypeIds = Object.keys(typeMixOverride).filter((id) => TYPES[id]); // ignore any stray/unknown key
  const typeIds = [...new Set([...baselineTypeIds, ...overrideTypeIds])];

  const mixItems = typeIds.map((typeId) => {
    const w = typeMixOverride[typeId];
    const weight = typeof w === "string" && w in QUALITATIVE_WEIGHT ? QUALITATIVE_WEIGHT[w] : QUALITATIVE_WEIGHT.medium;
    return { id: typeId, weight };
  });

  const fallbackType = TYPES.addition; // matches the original GENERATORS[bandKey]||GENERATORS.builder shape for an unrecognised band
  if (!mixItems.length) return fallbackType.generate(fallbackType.defaultParams);

  const typeId = weightedPick(mixItems).id;
  const def = TYPES[typeId];

  // Params: merge override over defaults, field-by-field, type-safe — a partial
  // override shouldn't blow away untouched fields; a type-mismatched value falls
  // back to its default rather than propagate garbage into the generator formula.
  const defaults = def.defaultParams || {};
  const cmsParams = typeParamsOverride[typeId];
  let params = defaults;
  if (cmsParams) {
    params = { ...defaults };
    for (const [key, defaultValue] of Object.entries(defaults)) {
      const cmsValue = cmsParams[key];
      if (cmsValue === undefined) continue;
      if (Array.isArray(defaultValue)) {
        if (Array.isArray(cmsValue) && cmsValue.length) params[key] = cmsValue;
      } else if (typeof cmsValue === typeof defaultValue) {
        params[key] = cmsValue;
      }
    }
  }

  // Slot items: override REPLACES the default list wholesale (naturally authored
  // as one list at a time), carrying through any extra default fields (sides,
  // factor, from/to) an override might not specify for a given item id.
  let slotItems = def.defaultSlotItems || null;
  const cmsItems = def.defaultSlotItems ? slotItemsOverride[typeId] : null;
  if (cmsItems && cmsItems.length) {
    slotItems = cmsItems.map((item) => {
      const numericWeight = typeof item.weight === "string" && item.weight in QUALITATIVE_WEIGHT ? QUALITATIVE_WEIGHT[item.weight] : QUALITATIVE_WEIGHT.medium;
      const matchingDefault = def.defaultSlotItems.find((d) => d.id === item.id) || {};
      return { ...item, ...matchingDefault, weight: numericWeight };
    });
  }

  // Level (LB-19 2d, Alan: "a grade 2 question with big numbers can be as hard as a grade 4
  // with simple numbers"). Two dimensions: the type's position in the band (what the question
  // is ABOUT) and the level 1–3 (what the numbers DO). The type-mix weight is only ever how
  // OFTEN a type appears; it never stands in for difficulty.
  const level = overrides && Number.isInteger(overrides.level) ? Math.max(1, Math.min(3, overrides.level)) : 2;
  const levelled = level === 2 ? params : levelParams(typeId, params, level, bandId, overrides && overrides.l3);
  const q = def.generate(levelled, slotItems);
  return q && typeof q === "object" ? { ...q, typeId, level, grade: mathsItemGrade(typeId, level, bandId, overrides && overrides.grades), topic: typeId } : q;
}
// Level 1 shrinks every numeric range toward its minimum (single digits, no carry); level 3
// pushes it out (two- and three-digit operands, carrying). Arrays (denominators, percents,
// notes…) are left alone. A per-type CMS override (`levels`) wins; `levels.fixed` pins all
// three to the defaults (time, shapes — where "bigger numbers" means nothing).
// bandId scopes the CMS `levels` lookup (band-first, see content.js); l3Overrides is the
// worker's way of passing a draft `l3` map (the sample preview has no bundle to read).
export function levelParams(typeId, params, level, bandId, l3Overrides) {
  const cms = cmsMathsTypeLevels(typeId, bandId);
  if (cms && cms.fixed) return params;
  const f = l3Overrides && typeof l3Overrides[typeId] === "number" && l3Overrides[typeId] >= 1 ? l3Overrides[typeId] : l3FactorFor(typeId, bandId);
  const out = { ...params };
  const rangeKeys = Object.keys(params).filter((k) => /Max$/.test(k) && typeof params[k] === "number");
  for (const maxKey of rangeKeys) {
    const minKey = maxKey.replace(/Max$/, "Min");
    const min = typeof params[minKey] === "number" ? params[minKey] : 1;
    const max = params[maxKey];
    if (max <= min) continue;
    if (level === 1) out[maxKey] = Math.max(min + 1, Math.round(min + (max - min) * 0.5));
    if (level === 3) out[maxKey] = Math.round(max + (max - min) * (f - 1));
  }
  for (const k of ["max", "target", "sumMax"]) if (typeof params[k] === "number" && !rangeKeys.includes(k)) { if (level === 1) out[k] = Math.max(2, Math.round(params[k] * 0.5)); if (level === 3) out[k] = Math.round(params[k] * f); }
  if (cms && cms[String(level)] && typeof cms[String(level)] === "object") for (const [k, v] of Object.entries(cms[String(level)])) if (k in params) out[k] = v;
  return out;
}
export function mathsItemGrade(typeId, level, bandId, gradeOverrides) {
  return Math.max(1, Math.min(5, mathsTypeGrade(typeId, bandId, gradeOverrides) + ((level || 2) - 2)));
}
// A type's grade (1–5) inside a band (Maths Ladder v2). Precedence: the band's published
// `grades` map → the registry's own `grade` (its grade in its home band) → position in the
// home band's list, which is what every grade used to be and is kept only so an unknown
// type still resolves. Position was the defect: Builder graded "£20 spend £4" at 5 and
// 2-digit multiplication in Challenger at 1, because that is where they sat in the file.
export function mathsTypeGrade(typeId, bandId, gradeOverrides) {
  const cmsG = gradeOverrides || (bandId ? cmsMathsTypeGrades(bandId) : null);
  const g = cmsG && Number.isInteger(cmsG[typeId]) ? cmsG[typeId] : null;
  if (g && g >= 1 && g <= 5) return g;
  const def = TYPES[typeId];
  if (def && Number.isInteger(def.grade)) return Math.max(1, Math.min(5, def.grade));
  const band = def && def.band;
  const ids = band ? (TYPE_IDS_BY_BAND[band] || []) : [];
  const i = ids.indexOf(typeId); const n = ids.length;
  return i < 0 || n <= 1 ? 3 : Math.min(5, 1 + Math.floor((i * 5) / n));
}
// Adaptive draw for maths: weight the band's types around a target grade (60% at, 20% one
// below, 20% one above), mastered types at 10%, before the normal CMS weights apply.
export function generateAdaptiveMathsSession(bandKey, size, adaptiveState, params) {
  const ids = mathsBandTypeIds(bandKey);
  if (!ids.length || !adaptiveState) return generateMathsSession(bandKey, size);
  const target = Math.max(1, Math.min(5, adaptiveState.target || 1));
  const P = params || { draw: { at: 60, below: 20, above: 20 }, retention: 10 };
  const mastered = (id) => !!(adaptiveState.topics && adaptiveState.topics[id] && adaptiveState.topics[id].mastered);
  const cmsWeights = cmsMathsTypeMix(bandKey) || {};
  const freq = (id) => { const w = cmsWeights[id]; return w === "none" ? 0 : w === "low" ? 1 : w === "high" ? 3 : 2; }; // frequency: how OFTEN, never how hard
  // Candidate (type, level) pairs with their item grade; the adaptive weight is by grade
  // distance from the target, multiplied by the type's frequency weight.
  // Maths Ladder v2: at Starter (target 1) the "one above" slot is the band's grade-2 types
  // at their normal numbers — nothing else. Not a grade-1 type at level 3 (the stretched
  // numbers: how 375 ÷ 15 reached a child who had just started Year 4), and not a grade-3
  // type at level 1 (small numbers do not make 2-digit multiplication a Starter skill). A
  // Starter's stretch is the next rung's skill, not the first rung's numbers made bigger
  // or a later rung's skill made smaller.
  const pairs = [];
  for (const id of ids) for (const level of [1, 2, 3]) {
    const g = mathsItemGrade(id, level, bandKey);
    let w = g === target ? P.draw.at : g === target - 1 ? P.draw.below : g === target + 1 ? P.draw.above : 0;
    if (target === 1 && g === 2 && (level === 3 || mathsTypeGrade(id, bandKey) >= 3)) w = 0;
    if (mastered(id)) w = level === 3 ? Math.round(P.retention) : 0; // a mastered skill returns at level 3 only, as a retention check
    w = w * freq(id);
    if (w > 0) pairs.push({ id, level, w });
  }
  if (!pairs.length) return generateMathsSession(bandKey, size);
  const typeParams = {}; const slotItems = {};
  for (const typeId of ids) { const p = cmsMathsTypeParams(typeId, bandKey); if (p) typeParams[typeId] = p; const si = cmsMathsSlotItems(typeId, bandKey); if (si) slotItems[typeId] = si; }
  const pickPair = () => { let r = Math.random() * pairs.reduce((a, p) => a + p.w, 0); for (const p of pairs) { r -= p.w; if (r <= 0) return p; } return pairs[pairs.length - 1]; };
  // The band's other types must be "none" for the pick to be the only draw — generateFromOverrides
  // unions the band's default set with the override keys and gives untouched types "medium".
  // …and the band's baseline types too: a baseline type the mix switched off is not in
  // `ids`, and generateFromOverrides would give it "medium" if the map stayed silent on it.
  const allIds = [...new Set([...(TYPE_IDS_BY_BAND[bandKey] || []), ...ids])];
  const only = (id) => Object.fromEntries(allIds.map((x) => [x, x === id ? "high" : "none"]));
  return buildUniqueSession(size, () => { const p = pickPair(); return generateFromOverrides(bandKey, { typeMix: only(p.id), typeParams, slotItems, level: p.level }); });
}

export function generateMathsQuestion(bandKey) {
  const cmsWeights = cmsMathsTypeMix(bandKey) || {};
  const typeParams = {};
  const slotItems = {};
  // mathsBandTypeIds, not the baseline: a type a band gained through its CMS mix used to
  // draw with the registry defaults even when band-scoped params were published for it.
  for (const typeId of mathsBandTypeIds(bandKey)) {
    const p = cmsMathsTypeParams(typeId, bandKey);
    if (p) typeParams[typeId] = p;
    const s = cmsMathsSlotItems(typeId, bandKey);
    if (s) slotItems[typeId] = s;
  }
  return generateFromOverrides(bandKey, { typeMix: cmsWeights, typeParams, slotItems });
}

export function generateMathsSession(bandKey, size) {
  return buildUniqueSession(size, () => generateMathsQuestion(bandKey));
}


// TT Streaks has the identical repeated-question failure mode as Maths Workout — a single
// table selected (or just an unlucky run) can draw the same fact twice in one session — so
// it gets the same dedup treatment. Tests.jsx builds its session from this rather than
// calling generateTTQuestion directly in a bare Array.from.
export function generateTTSession(tables, size) {
  return buildUniqueSession(size, () => generateTTQuestion(tables));
}
